import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { PROPERTY_TYPES, CLEANING_TYPES, FREQUENCIES } from "../content";
import { API, track } from "../lib/track";

const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:border-brand-teal focus:ring-2 focus:ring-teal-600/20 focus:outline-none";
const labelCls = "mb-1.5 block text-sm font-semibold text-brand-ink";

const Field = ({ label, children, required }) => (
  <div>
    <label className={labelCls}>
      {label} {required && <span className="text-brand-teal" aria-hidden="true">*</span>}
    </label>
    {children}
  </div>
);

const initial = {
  name: "", phone: "", email: "", suburb: "", propertyType: "", service: "",
  frequency: "", bedrooms: "", bathrooms: "", preferredDate: "", flexible: false,
  message: "", agency: "", numProperties: "", areas: "", company: "",
};

export const QuoteForm = ({ variant = "quote", defaultService = "", defaultSuburb = "", source = "website" }) => {
  const isProperty = variant === "property";
  const [form, setForm] = useState({ ...initial, service: defaultService, suburb: defaultSuburb });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, { ...form, kind: isProperty ? "property" : "quote" });
      track("form_submit", { form: isProperty ? "property_enquiry" : "quote", source });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong sending your enquiry. Please try again, or call us directly.");
    }
  };

  if (status === "sent") {
    return (
      <div data-testid="quote-form-success" className="rounded-3xl border border-teal-200 bg-teal-50 p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-brand-teal" />
        <h3 className="mt-5 font-display text-2xl font-bold text-brand-ink">Thanks for contacting Pure Bliss Clean.</h3>
        <p className="mx-auto mt-3 max-w-md text-slate-600">
          We've received your enquiry and will be in touch regarding your cleaning requirements.
        </p>
      </div>
    );
  }

  return (
    <form data-testid={isProperty ? "property-form" : "quote-form"} onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
      <input type="text" name="company" value={form.company} onChange={set("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="grid gap-5 sm:grid-cols-2">
        {isProperty && (
          <>
            <Field label="Agency / Business Name" required>
              <input data-testid="field-agency" required value={form.agency} onChange={set("agency")} className={inputCls} placeholder="Your agency or business" />
            </Field>
            <Field label="Number of Properties (if relevant)">
              <input data-testid="field-num-properties" value={form.numProperties} onChange={set("numProperties")} className={inputCls} placeholder="e.g. 12 rentals" />
            </Field>
          </>
        )}
        <Field label={isProperty ? "Contact Name" : "Name"} required>
          <input data-testid="field-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
        </Field>
        <Field label="Phone" required>
          <input data-testid="field-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Your best contact number" />
        </Field>
        <Field label="Email" required>
          <input data-testid="field-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
        </Field>
        <Field label="Suburb">
          <input data-testid="field-suburb" value={form.suburb} onChange={set("suburb")} className={inputCls} placeholder="e.g. Carrum Downs" />
        </Field>
        <Field label="Property Type">
          <select data-testid="field-property-type" value={form.propertyType} onChange={set("propertyType")} className={inputCls}>
            <option value="">Select property type</option>
            {PROPERTY_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Cleaning Required">
          <select data-testid="field-service" value={form.service} onChange={set("service")} className={inputCls}>
            <option value="">Select cleaning type</option>
            {CLEANING_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Frequency">
          <select data-testid="field-frequency" value={form.frequency} onChange={set("frequency")} className={inputCls}>
            <option value="">Select frequency</option>
            {FREQUENCIES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        {isProperty ? (
          <Field label="Areas / Suburbs">
            <input data-testid="field-areas" value={form.areas} onChange={set("areas")} className={inputCls} placeholder="e.g. Frankston, Cranbourne" />
          </Field>
        ) : (
          <>
            <Field label="Bedrooms">
              <select data-testid="field-bedrooms" value={form.bedrooms} onChange={set("bedrooms")} className={inputCls}>
                <option value="">Select</option>
                {["1", "2", "3", "4", "5", "6+"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Bathrooms">
              <select data-testid="field-bathrooms" value={form.bathrooms} onChange={set("bathrooms")} className={inputCls}>
                <option value="">Select</option>
                {["1", "2", "3", "4+"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Preferred Date">
              <input data-testid="field-date" type="date" value={form.preferredDate} onChange={set("preferredDate")} className={inputCls} />
            </Field>
            <div className="flex items-end pb-2">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-brand-ink">
                <input data-testid="field-flexible" type="checkbox" checked={form.flexible} onChange={set("flexible")} className="h-4 w-4 rounded border-slate-300 text-brand-teal accent-teal-700" />
                I'm flexible on the date
              </label>
            </div>
          </>
        )}
      </div>

      <div className="mt-5">
        <Field label="Tell us a little about what you need cleaned">
          <textarea data-testid="field-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="A few details about your property and what you're after" />
        </Field>
      </div>

      {status === "error" && (
        <p data-testid="quote-form-error" className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </p>
      )}

      <button
        data-testid="quote-form-submit"
        type="submit"
        disabled={status === "sending"}
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-teal px-8 py-4 font-display text-base font-bold text-white shadow-lg shadow-teal-900/15 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-teal-600 disabled:opacity-60 sm:w-auto"
      >
        {status === "sending" && <Loader2 className="h-5 w-5 animate-spin" />}
        {isProperty ? "Discuss Your Cleaning Requirements" : "Request My Free Quote"}
      </button>
      <p className="mt-4 text-xs text-slate-500">No obligation. Every property is different, so we'll chat about your requirements before talking pricing.</p>
    </form>
  );
};
